/**
 * SESSION
 * Item used in reducer
 */

import { getLocalizedText } from "@/utils/localized-text.utils"
import { Colors } from "./colors.model"
import { Topic } from "./topic.model"
import { uniqBy } from "lodash"
import { Axis } from "./axis.model"
import { MessageOptions } from "./message-options.model"
import { AxisRule } from "./axis-rule.model"
import { TemplateOptions } from "./template-options.model"
import { t } from "@/translate/t"
import { Question } from "./question.model"

export class Session {
  accountId: string = ""
  accountImage: string | null = null
  accountName: string = ""
  authorizeSeeResults: boolean = false
  ceilTesters: number = 5
  colors: Colors = new Colors()
  currentAxis: string | null = null
  currentQuestionSecondary: boolean = false
  backgroundImageColor: string | null = null
  imageUrl: string = require("@/assets/intro.png")
  feedbackId: string | null = null
  isPreview: boolean = false
  isTest: boolean = true
  isSurveyAdmin: boolean = false
  isTrialAccount: boolean = false
  language: string = ""
  languages: string[] = []
  messageOptions: MessageOptions = new MessageOptions()
  participationMin: number = 5
  resultsCode: string | null = null //code required in url fdr user to access results
  saveResults: boolean = false
  segmentationByAxesRules: AxisRule[] = []
  status: string = ""
  surveyName: string = ""
  showTestModal: boolean = false
  templateOptions: TemplateOptions = new TemplateOptions()
  topics: Topic[] = []
  skippedTopics: Topic[] = []
  jumpOriginTopicId: string | null = null
  userAttributes: string[] = []
  usersForTest: string[] = []
  accessCode: string | null = null

  constructor(session: Partial<Session> = {}) {
    if (!session) session = new Session()
    session.colors = new Colors(session.colors)
    session.messageOptions = new MessageOptions(session.messageOptions)
    session.templateOptions = new TemplateOptions(session.templateOptions)
    session.topics = session.topics ? session.topics.map((x) => new Topic(x)) : []
    session.skippedTopics = session.skippedTopics ? session.skippedTopics.map((x) => new Topic(x)) : []
    Object.assign(this, session)
  }

  get axes(): Axis[] {
    const axes = this.topics.map((x) => x.Axis).filter((x) => x.id !== "")
    return uniqBy(axes, "id")
  }

  get axesCount(): number {
    return this.axes.length
  }

  get axesCountDone(): number {
    const axes = uniqBy(
      this.topicsDone.map((x) => x.Axis),
      "id"
    )
    if (this.topicsDone.filter((x) => x.AxisId === this.currentAxis).length === 0) {
      return axes.length + 1
    } else {
      return axes.length
    }
  }

  get topicsDone(): Topic[] {
    return this.topics.filter((x) => x.done)
  }

  get topicsUndone(): Topic[] {
    return this.topics.filter((x) => !x.done)
  }

  getMessageOptions(language, type: "title" | "content" | "titleSecondary") {
    return getLocalizedText(this.messageOptions[type], language, this.languages)
  }

  //Get label for every response
  getResponseLabel(
    responseCount: number,
    key: string | number,
    labelPrefix: string,
    question?: Question,
    isDescription?: boolean
  ): string | null {
    if (responseCount === 4) {
      responseCount = 5
      if (key === 3 || key === 4) {
        key = key + 1
      }
    }

    const defaultLabel = isDescription ? null : t(labelPrefix + "_" + key)

    //Use case custom response for each question
    //> the text written for the language of the participant
    //> else the old single text (written before the languages) : displayed to everyone
    //(only if the question still uses them : "Retour aux valeurs par défaut" in the editor switches them off)
    if (this.templateOptions.editResponseLabelByQuestion && question?.options?.isCustomResponseLabel) {
      const base = labelPrefix + "_" + key
      const custom = question.options[base + "_" + this.language] || question.options[base]
      if (custom) {
        return custom
      }
    }

    if (this.templateOptions["response" + responseCount + "Label"]) {
      const label =
        this.templateOptions[
          "response" + responseCount + "Label_" + key + "_" + (isDescription ? "description_" : "") + this.language
        ]
      return label || label?.length > 0 ? label : defaultLabel
    } else {
      return defaultLabel
    }
  }
}
